import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import {
  Plus, Search, MoreHorizontal, Eye, EyeOff,
  Copy, Trash2, ExternalLink, BarChart3, FileText, Globe,
  Clock, Users, LayoutTemplate, Activity, Grid3X3, List, ArrowUpDown, MousePointerClick, Filter
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { EmptyState } from '@/components/data-display';
import { toast } from 'sonner';
import { landingPagesApi } from '@/api/landingPages';
import { useAppSettings } from '@/contexts/AppSettingsContext';
import TrackingFields, { emptyTracking, trackingPayload } from '@/components/TrackingFields';
import { useRegionRequired } from '@/lib/partner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const PER_PAGE = 50;

const SORT_OPTIONS = [
  { value: 'name_asc', label: 'Name (A-Z)' },
  { value: 'name_desc', label: 'Name (Z-A)' },
  { value: 'views_desc', label: 'Most Views' },
  { value: 'views_asc', label: 'Fewest Views' },
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
];

const SORT_MAP = {
  newest:     ['created_at', 'desc'],
  oldest:     ['created_at', 'asc'],
  name_asc:   ['title', 'asc'],
  name_desc:  ['title', 'desc'],
  views_desc: ['total_views', 'desc'],
  views_asc:  ['total_views', 'asc'],
};

const VIEW_MODES = [
  { value: 'table', icon: List },
  { value: 'grid', icon: Grid3X3 },
];

export default function LandingPages() {
  const regionRequired = useRegionRequired();
  const navigate = useNavigate();
  const { urls, buildLandingPageUrl } = useAppSettings();
  const [pages, setPages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, page: 1, per_page: PER_PAGE, has_more: false });
  const [page, setPage] = useState(1);

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 300);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState('table');
  const [activeTab, setActiveTab] = useState('all');

  // Create dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [newPageTitle, setNewPageTitle] = useState('');
  const [newPageTracking, setNewPageTracking] = useState(emptyTracking);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchQuery, statusFilter, activeTab, sortBy]);

  const fetchPages = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: String(page),
        per_page: String(PER_PAGE),
      };
      if (debouncedSearchQuery.trim()) params.search = debouncedSearchQuery.trim();
      const effectiveStatus = activeTab !== 'all' ? activeTab : (statusFilter !== 'all' ? statusFilter : null);
      if (effectiveStatus) params.status = effectiveStatus;
      const [sortField, sortDir] = SORT_MAP[sortBy] || SORT_MAP.newest;
      params.sort_by = sortField;
      params.sort_order = sortDir;

      const data = await landingPagesApi.list(params);
      if (data.success) {
        setPages(data.data?.items || data.data || []);
        setPagination(data.data?.pagination || data.pagination || { total: (data.data || []).length, page, per_page: PER_PAGE, has_more: false });
      }
    } catch (error) {
      console.error('Error fetching pages:', error);
      toast.error('Failed to load landing pages');
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearchQuery, statusFilter, activeTab, sortBy]);

  const fetchTemplates = useCallback(async () => {
    try {
      const data = await landingPagesApi.getTemplates();
      if (data.success) {
        setTemplates(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  }, []);

  useEffect(() => {
    fetchPages();
    fetchTemplates();
  }, [fetchPages, fetchTemplates]);

  // Search, status, and sort are handled server-side. No client-side filtering needed.
  const filteredPages = pages;

  // Stats come from the server: the list above is one page, so counting it
  // would cap every card at PER_PAGE.
  const [stats, setStats] = useState({ total: 0, publishedCount: 0, draftCount: 0, totalViews: 0, uniqueViews: 0, totalSubmissions: 0, viewsSince: null });

  const fetchStats = useCallback(async () => {
    try {
      const data = await landingPagesApi.stats();
      if (data.success) {
        const d = data.data || {};
        setStats({
          total: d.total || 0,
          publishedCount: d.published || 0,
          draftCount: d.draft || 0,
          totalViews: d.total_views || 0,
          uniqueViews: d.unique_views || 0,
          totalSubmissions: d.form_submissions || 0,
          viewsSince: d.views_tracked_since || null,
        });
      }
    } catch (error) {
      console.error('Error fetching landing page stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // After a change: reload the current page and the headline counts.
  const refresh = () => {
    fetchPages();
    fetchStats();
  };

  const handleCreatePage = async () => {
    if (!newPageTitle.trim()) {
      toast.error('Please enter a page title');
      return;
    }
    if (regionRequired && !newPageTracking.region_id) {
      toast.error('Please select a region before creating this page.');
      return;
    }
    setCreating(true);
    try {
      const payload = {
        title: newPageTitle,
        template_type: selectedTemplate?.type || 'blank',
        tracking: trackingPayload(newPageTracking),
      };

      // If a template with an ID is selected (system or custom template),
      // include the template_id so the backend can copy its sections
      if (selectedTemplate?.id) {
        payload.template_id = selectedTemplate.id;
      }

      const data = await landingPagesApi.create(payload);
      if (data.success) {
        toast.success('Page created!');
        setCreateDialogOpen(false);
        setNewPageTitle('');
        setNewPageTracking(emptyTracking);
        setSelectedTemplate(null);
        navigate(`/pages/${data.data.id}`);
      } else {
        toast.error(data.detail || 'Failed to create page');
      }
    } catch (error) {
      toast.error('Failed to create page');
    } finally {
      setCreating(false);
    }
  };

  const handlePublish = async (id) => {
    try {
      const data = await landingPagesApi.publish(id);
      if (data.success) {
        toast.success('Page published!');
        refresh();
      }
    } catch (error) {
      toast.error('Failed to publish page');
    }
  };

  const handleUnpublish = async (id) => {
    try {
      const data = await landingPagesApi.unpublish(id);
      if (data.success) {
        toast.success('Page unpublished');
        refresh();
      }
    } catch (error) {
      toast.error('Failed to unpublish page');
    }
  };

  const handleDuplicate = async (id) => {
    try {
      const data = await landingPagesApi.duplicate(id);
      if (data.success) {
        toast.success('Page duplicated!');
        refresh();
      }
    } catch (error) {
      toast.error('Failed to duplicate page');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to archive this page?')) return;
    try {
      const data = await landingPagesApi.delete(id);
      if (data.success) {
        toast.success('Page archived');
        refresh();
      }
    } catch (error) {
      toast.error('Failed to archive page');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const getStatusBadge = (status) => {
    const config = {
      draft: { className: 'bg-amber-500/10 text-amber-600 border-amber-200' },
      published: { className: 'bg-green-500/10 text-green-600 border-green-200' },
      archived: { className: 'bg-gray-500/10 text-gray-600 border-gray-200' }
    };
    const { className } = config[status] || config.draft;
    return (
      <Badge variant="outline" className={className}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getTemplateIcon = (type) => {
    const icons = {
      property_listing: '🏠',
      agent_profile: '👤',
      open_house: '📅',
      general_marketing: '📢',
      contact_only: '✉️',
      blank: '📄'
    };
    return icons[type] || '📄';
  };

  return (
    <div className="space-y-6" data-testid="landing-pages-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Landing Pages</h1>
          <p className="text-muted-foreground">Create and manage branded landing pages with contact forms</p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)} data-testid="create-page-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create Page
        </Button>
      </div>

      {/* Create Page Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Create New Landing Page</DialogTitle>
            <DialogDescription>Choose a template or start from scratch</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Page Title</label>
              <Input
                placeholder="e.g. Property Showcase - 123 Main Street"
                value={newPageTitle}
                onChange={(e) => setNewPageTitle(e.target.value)}
                className="mt-1"
                data-testid="new-page-title-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Select Template</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
                {templates.map((template) => (
                  <div
                    key={template.id}
                    onClick={() => setSelectedTemplate(template)}
                    className={`p-4 border rounded-lg cursor-pointer transition-all hover:border-primary ${
                      selectedTemplate?.id === template.id
                        ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                        : 'border-border'
                    }`}
                    data-testid={`template-${template.type}`}
                  >
                    <div className="text-2xl mb-2">{getTemplateIcon(template.type)}</div>
                    <h4 className="font-medium text-sm">{template.name}</h4>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                      {template.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Organization</label>
              <div className="mt-2">
                <TrackingFields value={newPageTracking} onChange={setNewPageTracking} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreatePage} disabled={creating || !newPageTitle.trim() || (regionRequired && !newPageTracking.region_id)}>
              {creating ? 'Creating...' : 'Create Page'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-primary/10 to-primary/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <LayoutTemplate className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-xs text-muted-foreground">Total Pages</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/10 rounded-lg">
                <Globe className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.publishedCount}</p>
                <p className="text-xs text-muted-foreground">Published</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <Eye className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.totalViews.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">Total Views</p>
                {/* Views weren't recorded from 29 Mar 2026 until the view
                    beacon went live; say from when the figure is real. */}
                <p className="text-xs text-muted-foreground" data-testid="views-since">
                  {stats.viewsSince
                    ? `${stats.uniqueViews.toLocaleString()} unique, counted since ${new Date(stats.viewsSince).toLocaleDateString()}`
                    : 'Not counted yet'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/10 rounded-lg">
                <MousePointerClick className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.totalSubmissions.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">Submissions</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Status */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="all" className="flex items-center gap-2">
            <LayoutTemplate className="w-4 h-4" />
            All ({stats.total})
          </TabsTrigger>
          <TabsTrigger value="published" className="flex items-center gap-2">
            <Globe className="w-4 h-4" />
            Published ({stats.publishedCount})
          </TabsTrigger>
          <TabsTrigger value="draft" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Draft ({stats.draftCount})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Search and Filters Bar */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by title, slug, description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="search-input"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[150px]" data-testid="sort-select">
              <ArrowUpDown className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Sort" />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex border rounded-md">
            {VIEW_MODES.map(mode => (
              <Button
                key={mode.value}
                variant={viewMode === mode.value ? 'secondary' : 'ghost'}
                size="icon"
                onClick={() => setViewMode(mode.value)}
                className="rounded-none first:rounded-l-md last:rounded-r-md"
                data-testid={`view-${mode.value}`}
              >
                <mode.icon className="w-4 h-4" />
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Results count */}
      {searchQuery && (
        <p className="text-sm text-muted-foreground">
          Found {pagination.total ?? filteredPages.length} result{(pagination.total ?? filteredPages.length) !== 1 ? 's' : ''} for "{searchQuery}"
        </p>
      )}

      {/* Grid or Table View */}
      {loading ? (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Page</TableHead>
                  <TableHead>URL</TableHead>
                  <TableHead>Views</TableHead>
                  <TableHead>Submissions</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-4 w-[200px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[180px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[60px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[60px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[70px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[80px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[30px]" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : filteredPages.length === 0 ? (
        <EmptyState
          icon={LayoutTemplate}
          title={searchQuery || activeTab !== 'all' ? 'No matching pages' : 'No landing pages yet'}
          description={searchQuery || activeTab !== 'all'
            ? 'Try adjusting your search or filters.'
            : 'Landing pages are standalone destinations you build inside LinkHub — useful for campaigns, lead capture, and one-off promos. Pick a template and customise it to launch your first page.'}
          actionLabel={!searchQuery && activeTab === 'all' ? 'Create Page' : undefined}
          onAction={!searchQuery && activeTab === 'all' ? () => setCreateDialogOpen(true) : undefined}
        />
      ) : viewMode === 'grid' ? (
        /* Grid View */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="pages-grid">
          {filteredPages.map((page) => (
            <Card key={page.id} className="hover:shadow-md transition-shadow" data-testid={`page-card-${page.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="text-2xl">{getTemplateIcon(page.template_type)}</div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem asChild>
                        <Link to={`/pages/${page.id}`}>
                          <FileText className="w-4 h-4 mr-2" />
                          Edit Page
                        </Link>
                      </DropdownMenuItem>
                      {page.status === 'published' && (
                        <DropdownMenuItem asChild>
                          <a href={`https://${buildLandingPageUrl(page.slug)}`} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="w-4 h-4 mr-2" />
                            View Live
                          </a>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => handleDuplicate(page.id)}>
                        <Copy className="w-4 h-4 mr-2" />
                        Duplicate
                      </DropdownMenuItem>
                      <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(page.id)}>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Archive
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <Link to={`/pages/${page.id}`} className="font-medium hover:text-primary truncate block mb-1">
                  {page.title}
                </Link>
                <code className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded truncate block mb-3">
                  /p/{page.slug}
                </code>
                <div className="flex items-center justify-between">
                  {getStatusBadge(page.status)}
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Eye className="w-3 h-3" />
                      {(page.total_views || 0).toLocaleString()}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {(page.form_submissions || 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        /* Table View */
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Page</TableHead>
                  <TableHead>URL</TableHead>
                  <TableHead>Views</TableHead>
                  <TableHead>Submissions</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPages.map((page) => (
                  <TableRow key={page.id} data-testid={`page-row-${page.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="text-xl">{getTemplateIcon(page.template_type)}</div>
                        <div>
                          <Link
                            to={`/pages/${page.id}`}
                            className="font-medium hover:text-primary transition-colors"
                          >
                            {page.title}
                          </Link>
                          {page.description && (
                            <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                              {page.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <code className="text-sm bg-muted px-2 py-0.5 rounded truncate max-w-[160px]">
                          /p/{page.slug}
                        </code>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => copyToClipboard(`${window.location.origin}/p/${page.slug}`)}
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Eye className="w-3 h-3 text-muted-foreground" />
                        <span>{(page.total_views || 0).toLocaleString()}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Users className="w-3 h-3 text-muted-foreground" />
                        <span>{(page.form_submissions || 0).toLocaleString()}</span>
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(page.status)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(page.created_at)}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link to={`/pages/${page.id}`}>
                              <FileText className="w-4 h-4 mr-2" />
                              Edit Page
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link to={`/pages/details/${page.id}`}>
                              <BarChart3 className="w-4 h-4 mr-2" />
                              View Details
                            </Link>
                          </DropdownMenuItem>
                          {page.status === 'published' && (
                            <DropdownMenuItem asChild>
                              <a href={`https://${buildLandingPageUrl(page.slug)}`} target="_blank" rel="noopener noreferrer">
                                <ExternalLink className="w-4 h-4 mr-2" />
                                View Live
                              </a>
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => copyToClipboard(`https://${buildLandingPageUrl(page.slug)}`)}>
                            <Copy className="w-4 h-4 mr-2" />
                            Copy URL
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {page.status === 'draft' ? (
                            <DropdownMenuItem onClick={() => handlePublish(page.id)}>
                              <Globe className="w-4 h-4 mr-2" />
                              Publish
                            </DropdownMenuItem>
                          ) : page.status === 'published' && (
                            <DropdownMenuItem onClick={() => handleUnpublish(page.id)}>
                              <EyeOff className="w-4 h-4 mr-2" />
                              Unpublish
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => handleDuplicate(page.id)}>
                            <Copy className="w-4 h-4 mr-2" />
                            Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => handleDelete(page.id)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Archive
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {(pagination.total > 0 || pages.length > 0) && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground" data-testid="pagination-summary">
            Page {pagination.page || page} of {Math.max(1, Math.ceil((pagination.total || 0) / (pagination.per_page || PER_PAGE)))} — {pagination.total || pages.length} total
          </p>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  data-testid="pagination-prev"
                  aria-disabled={page <= 1}
                  onClick={(e) => {
                    e.preventDefault();
                    if (page > 1) setPage(p => p - 1);
                  }}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink href="#" isActive onClick={(e) => e.preventDefault()}>
                  {pagination.page || page}
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  data-testid="pagination-next"
                  aria-disabled={!pagination.has_more}
                  onClick={(e) => {
                    e.preventDefault();
                    if (pagination.has_more) setPage(p => p + 1);
                  }}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
}
